// Shared accessor for the in-progress booking draft stored in
// sessionStorage. Two surfaces read/write the same per-trailer draft:
//   - The trailer detail page (App.js): seeds + persists check-in /
//     check-out / times so a returning user sees what they previously
//     picked and so the booking form can pre-fill from the same source.
//   - The booking form (BookingForm.js): full-shape persistence of all
//     form fields (driver info, addons, signature progress, etc.).
//
// One module so the key prefix + shape live in one place — historically
// duplicating the prefix led to drift and the "old dates re-appear on
// fresh URL" bug because the two consumers disagreed about the source
// of truth.

export const STORAGE_KEY_PREFIX = 'booking_progress_v3_';

function keyFor(trailerId) {
  return `${STORAGE_KEY_PREFIX}${trailerId}`;
}

export function readDraft(trailerId) {
  if (!trailerId) return null;
  try {
    const raw = sessionStorage.getItem(keyFor(trailerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function readDraftDates(trailerId) {
  const d = readDraft(trailerId);
  if (!d) return null;
  return {
    checkIn: d.checkIn || '',
    checkOut: d.checkOut || '',
    pickupTime: d.pickupTime || '',
    returnTime: d.returnTime || '',
  };
}

// Merge-update the date fields without disturbing the rest of the draft.
// Pass `''` (or omit) for fields the caller doesn't want to set; missing
// keys leave the existing value untouched.
export function writeDraftDates(trailerId, patch) {
  if (!trailerId || !patch || typeof patch !== 'object') return;
  try {
    const existing = readDraft(trailerId) || {};
    const next = { ...existing };
    if ('checkIn' in patch) next.checkIn = patch.checkIn || '';
    if ('checkOut' in patch) next.checkOut = patch.checkOut || '';
    if ('pickupTime' in patch) next.pickupTime = patch.pickupTime || '';
    if ('returnTime' in patch) next.returnTime = patch.returnTime || '';
    sessionStorage.setItem(keyFor(trailerId), JSON.stringify(next));
  } catch {
    // Storage quota / serialization failures are non-fatal — the form
    // can still operate against in-memory state, the user just loses
    // the cross-surface hydration benefit.
  }
}
